import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export type WhatsAppSearchSelectOption = {
  value: string;
  label: string;
  hint?: string;
  group?: string;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: WhatsAppSearchSelectOption[];
  placeholder?: string;
  emptyLabel?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  allowEmpty?: boolean;
};

export default function WhatsAppSearchSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyLabel = 'Select…',
  searchPlaceholder = 'Search…',
  disabled,
  className = '',
  allowEmpty = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.label || (value ? value : emptyLabel);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label} ${o.value} ${o.hint || ''} ${o.group || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const grouped = useMemo(() => {
    const groups = new Map<string, WhatsAppSearchSelectOption[]>();
    for (const opt of filtered) {
      const key = opt.group || '';
      const list = groups.get(key) || [];
      list.push(opt);
      groups.set(key, list);
    }
    return Array.from(groups.entries());
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setQuery('');
        }}
        className="flex w-full items-center justify-between gap-1 rounded-lg border border-gray-200 px-3 py-2 text-left text-sm dark:border-gray-600 dark:bg-gray-800 disabled:opacity-50"
      >
        <span className={`truncate ${selected ? '' : 'text-gray-400'}`}>{selectedLabel}</span>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
      </button>
      {open && (
        <div className="absolute z-[80] mt-1 w-full min-w-[220px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-900">
          <div className="flex items-center gap-1.5 border-b px-2 py-1.5 dark:border-gray-700">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent py-1 text-sm outline-none"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {allowEmpty ? (
              <li>
                <button
                  type="button"
                  className="w-full px-3 py-1.5 text-left text-sm text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                  }}
                >
                  {placeholder}
                </button>
              </li>
            ) : null}
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">No matches</li>
            ) : (
              grouped.map(([group, items]) => (
                <li key={group || '_all'}>
                  {group ? (
                    <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      {group}
                    </p>
                  ) : null}
                  {items.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={`w-full px-3 py-1.5 text-left text-sm hover:bg-indigo-50 dark:hover:bg-indigo-950/40 ${
                        o.value === value ? 'bg-indigo-50 font-medium dark:bg-indigo-950/30' : ''
                      }`}
                      onClick={() => {
                        onChange(o.value);
                        setOpen(false);
                      }}
                    >
                      <span className="block truncate">{o.label}</span>
                      {o.hint || o.value !== o.label ? (
                        <span className="block truncate text-[10px] text-gray-400">
                          {o.hint || o.value}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
